import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import Footer from 'src/layouts/dashboard/footer/footer';
import FooterSection from 'src/layouts/dashboard/footer/footerSection';
import { Routes } from 'src/routes';
import MainPage from './MainPage';
import MenuBarComponent from './components/navbar/MenuBar';
import ProfileSection from './components/navbar/profile-section/ProfileSection';
import LiveChatNotificationBridge from './components/live-chat/LiveChatNotificationBridge';
import SupportAppNotification from './components/live-chat/SupportAppNotification';
import Header from './layouts/dashboard/header/header.component';
import MobileHeader from './layouts/dashboard/header/mobile/mobile-header.component';
import { applySetupCompletionToMenu, getSettingsMenu, navSidebar } from './layouts/dashboard/leftNavigation/config';
import { shouldRouteToGetStarted } from './pages/get-started/getStartedShared';
import HttpServices from './services/httpService';
import { userModuleURL } from './services/urlService';
import Spinner from './utils/Loader/spinner';
import { CommonHelper } from './utils/commonHelper';
import { addLiveChatSocketListener, connectLiveChatSocket } from './utils/liveChatSocket';
import { LocalStorage } from './utils/storage/local-storage';
import { SessionStorage } from './utils/storage/session-storage';
//import SupportChat from './components/support-chat/SupportChat';

const MainComponent = ({ isDesktop, handleInitCall }) => {
  const { t: translate } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const privateRoutes = Routes.PrivateRoute();
  const publicRoutes = Routes.PublicRoutes();

  let settingsMenu = getSettingsMenu(translate);

  const mainMenuItems = navSidebar(translate);

  const [processedMenuItems, setProcessedMenuItems] = useState([]);
  const [processedSettingsMenuItems, setProcessedSettingsMenuItems] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [showSideBar, setSideBar] = useState(false);
  const [headerName, setHeaderName] = useState('');
  const [isMainMenuBarView, setMainMenuBarView] = useState(true);
  const [isRouteLoaded, setRouteLoaded] = useState(false);
  const [brandPermissions, setBrandPermissions] = useState(CommonHelper.GetBrandInfoFromLocalStorage('permissions'));
  const [setupStatus, setSetupStatus] = useState(null);
  const [whatsappUnreadCount, setWhatsappUnreadCount] = useState(0);
  const [facebookUnreadCount, setFacebookUnreadCount] = useState(0);
  const [instagramUnreadCount, setInstagramUnreadCount] = useState(0);
  const userOrgDetails = CommonHelper.GetBrandInfoFromLocalStorage();
  const [routes, setRoutes] = useState(publicRoutes);
  const [isOrgUser, setOrgUser] = useState(false);

  const fetchSetupStatus = useCallback(() => {
    if (!CommonHelper.CheckUserLoggedIn()) {
      setSetupStatus(null);
      return;
    }

    HttpServices.Get({
      url: userModuleURL.getChatSetupStatus,
      successCallback: (data) => setSetupStatus(data || null),
      failureCallback: () => setSetupStatus(null),
    });
  }, []);

  const fetchFacebookUnreadCount = useCallback(() => {
    if (!CommonHelper.CheckUserLoggedIn()) {
      setFacebookUnreadCount(0);
      return;
    }

    HttpServices.Get({
      url: `${userModuleURL.connectedProfiles}?service_name=facebook`,
      successCallback: (data) => {
        const totalUnread = (data?.accounts || []).reduce((total, account) => total + Number(account.unread_count || 0), 0);
        setFacebookUnreadCount(totalUnread);
      },
      failureCallback: () => setFacebookUnreadCount(0),
    });
  }, []);

  const fetchWhatsappUnreadCount = useCallback(() => {
    if (!CommonHelper.CheckUserLoggedIn()) {
      setWhatsappUnreadCount(0);
      return;
    }

    HttpServices.Get({
      url: `${userModuleURL.connectedProfiles}?service_name=whatsapp`,
      successCallback: (data) => {
        const totalUnread = (data?.accounts || []).reduce((total, account) => total + Number(account.unread_count || 0), 0);
        setWhatsappUnreadCount(totalUnread);
      },
      failureCallback: () => setWhatsappUnreadCount(0),
    });
  }, []);

  const fetchInstagramUnreadCount = useCallback(() => {
    if (!CommonHelper.CheckUserLoggedIn()) {
      setInstagramUnreadCount(0);
      return;
    }

    HttpServices.Get({
      url: `${userModuleURL.connectedProfiles}?service_name=instagram`,
      successCallback: (data) => {
        const totalUnread = (data?.accounts || []).reduce((total, account) => total + Number(account.unread_count || 0), 0);
        setInstagramUnreadCount(totalUnread);
      },
      failureCallback: () => setInstagramUnreadCount(0),
    });
  }, []);

  useEffect(() => {
    preProcessMenuItems();
  }, []);

  useEffect(() => {
    if (shouldRouteToGetStarted(location.pathname)) {
      setHeaderName('');
      setSideBar(false);
      setMainMenuBarView(false);
      setMenuItems([]);
      navigate('/get-started', { replace: true, state: { from: location } });
      return;
    }

    handleRouteLoading(false);
    handleSideBar();
    if (!isOrgUser) {
      if (CommonHelper.GetBrandInfoFromLocalStorage('is_org_user')) {
        setOrgUser(true);
      }
    }
  }, [location.pathname, processedMenuItems, processedSettingsMenuItems]);

  useEffect(() => {
    preProcessMenuItems();
  }, [brandPermissions, whatsappUnreadCount, facebookUnreadCount, instagramUnreadCount, setupStatus]);

  useEffect(() => {
    fetchSetupStatus();
  }, [brandPermissions, location.pathname, fetchSetupStatus]);

  useEffect(() => {
    fetchWhatsappUnreadCount();
    fetchFacebookUnreadCount();
    fetchInstagramUnreadCount();
  }, [brandPermissions, location.pathname, fetchWhatsappUnreadCount, fetchFacebookUnreadCount, fetchInstagramUnreadCount]);

  useEffect(() => {
    connectLiveChatSocket();
    const removeListener = addLiveChatSocketListener((data) => {
      if (data.type === 'whatsapp:message:new') {
        fetchWhatsappUnreadCount();
      }
      if (data.type === 'facebook:message:new') {
        fetchFacebookUnreadCount();
      }
      if (data.type === 'instagram:message:new') {
        fetchInstagramUnreadCount();
      }
    });
    return removeListener;
  }, [brandPermissions, fetchWhatsappUnreadCount, fetchFacebookUnreadCount, fetchInstagramUnreadCount]);

  useEffect(() => {
    window.addEventListener('brand2chat:setup-status-refresh', fetchSetupStatus);
    return () => window.removeEventListener('brand2chat:setup-status-refresh', fetchSetupStatus);
  }, [fetchSetupStatus]);

  useEffect(() => {
    const refreshSocialUnreadCount = (event) => {
      const service = event?.detail?.service || '';
      if (!service || service === 'whatsapp') {
        fetchWhatsappUnreadCount();
      }
      if (!service || service === 'facebook') {
        fetchFacebookUnreadCount();
      }
      if (!service || service === 'instagram') {
        fetchInstagramUnreadCount();
      }
    };
    window.addEventListener('brand2chat:social-unread-refresh', refreshSocialUnreadCount);
    return () => window.removeEventListener('brand2chat:social-unread-refresh', refreshSocialUnreadCount);
  }, [fetchWhatsappUnreadCount, fetchFacebookUnreadCount, fetchInstagramUnreadCount]);

  // useEffect(() => {
  //   handleTrailPlanPopup();
  // }, [processedMenuItems, processedSettingsMenuItems]);

  const handleRouteLoading = (isRouteLoaded) => {
    setRouteLoaded(isRouteLoaded);
    LocalStorage.setItem('isRouteLoaded', JSON.stringify(isRouteLoaded));
  };

  const checkAndLoadRoutes = () => {
    const isUserLoggedIn = CommonHelper.CheckUserLoggedIn();
    if (isUserLoggedIn) {
      let allRoutes = getAllRoutes();
      setRoutes([...allRoutes]);
    } else {
      setRoutes([...publicRoutes]);
    }
    handleRouteLoading(true);
  };

  const getAllRoutes = () => {
    let allRoutes = privateRoutes.concat(publicRoutes);
    return allRoutes;
  };

  const handleSideBar = () => {
    if (location.pathname === '/get-started') {
      setHeaderName('');
      setSideBar(false);
      setMainMenuBarView(false);
      setMenuItems([]);
      checkAndLoadRoutes();
      return;
    }

    if (processedMenuItems.length > 0) {
      let route = Routes.PrivateRoute().find((a) => a.path === location.pathname.replace('/', ''));
      if (route) {
        let hName = GetHeaderName(processedMenuItems);
        if (hName) {
          setMainMenuBarView(true);
          setMenuItems([...processedMenuItems]);
        } else {
          setMainMenuBarView(false);
          hName = GetHeaderName(processedSettingsMenuItems);
          setMenuItems([...processedSettingsMenuItems]);
        }
        setHeaderName(hName);
      }

      let showSidebar = !!route;
      setSideBar(showSidebar);
      checkAndLoadRoutes();
    } else {
      preProcessMenuItems();
    }
  };

  const GetHeaderName = (menuItems) => {
    let headerName = '';
    for (var i = 0; i < menuItems.length; i++) {
      var item = menuItems[i];
      let pathMenu = item.menu.find((a) => a.path === location.pathname);
      if (pathMenu) {
        headerName = pathMenu.title;
        break;
      }
    }
    return headerName;
  };

  const preProcessMenuItems = () => {
    if (brandPermissions) {
      let processedMenuItems = [];
      let processedSettingsMenuItems = [];
      mainMenuItems.map((menuItem) => {
        let groupMenuItems = [];
        menuItem.menu.map((item) => {
          let isShowMenuBar = checkForPermission(item.permissions);
          if (isShowMenuBar) {
            const socialUnreadCounts = {
              '/whatsapp-chat': whatsappUnreadCount,
              '/facebook-chat': facebookUnreadCount,
              '/instagram-chat': instagramUnreadCount,
            };
            const unreadCount = Object.prototype.hasOwnProperty.call(socialUnreadCounts, item.path)
              ? location.pathname === item.path
                ? 0
                : socialUnreadCounts[item.path]
              : item.unreadCount;
            groupMenuItems.push({
              ...item,
              unreadCount,
            });
          }
        });
        if (groupMenuItems?.length > 0) {
          let pMenuItems = {
            title: menuItem.title,
            menu: [...groupMenuItems],
          };
          processedMenuItems.push(pMenuItems);
        }
      });
      settingsMenu.map((menuItem) => {
        let groupMenuItems = [];
        menuItem.menu.map((item) => {
          let isShowMenuBar = checkForPermission(item.permissions);
          if (item?.portalAdmin) {
            isShowMenuBar = userOrgDetails.is_portal_admin;
            //For Testing
            // isShowMenuBar = true;
          }
          if (isShowMenuBar) {
            groupMenuItems.push(item);
          }
        });
        if (groupMenuItems?.length > 0) {
          let pMenuItems = {
            title: menuItem.title,
            menu: [...groupMenuItems],
          };
          processedSettingsMenuItems.push(pMenuItems);
        }
      });
      setProcessedMenuItems([...applySetupCompletionToMenu(processedMenuItems, setupStatus)]);
      setProcessedSettingsMenuItems([...processedSettingsMenuItems]);
    } else {
      handleRouteLoading(true);
    }
  };

  const checkForPermission = (permissions) => {
    let isPermissionAvailable = false;
    if (permissions) {
      if (brandPermissions[permissions]) {
        isPermissionAvailable = true;
      } else {
        isPermissionAvailable = false;
      }
    }
    return isPermissionAvailable;
  };

  const backButtonClick = () => {
    navigate(CommonHelper.GetDefaultPermissionRoute(brandPermissions));
    setMenuItems([...mainMenuItems]);
  };

  const handleChangeBrand = (brandId) => {
    handleRouteLoading(false);
    let data = CommonHelper.GetBrandInfoFromLocalStorage();
    if (data) {
      SessionStorage.setItem('current_brand_id', JSON.stringify(brandId));
      let brand_details = data.brand_details.find((a) => a.brand_id === brandId);
      if (brand_details) {
        setBrandPermissions(brand_details.permission_details);
        // let selectedLanguage = brand_details.language ? brand_details.language : 'english';
        // i18n.changeLanguage(selectedLanguage);
        // localStorage.setItem('selectedLanguage', selectedLanguage);
      }
      LocalStorage.setItem('initial-load', JSON.stringify(data));
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('brand2chat:brand-changed', { detail: { brandId } }));
    }
    navigate(location.pathname, { state: null });
  };

  // const handleTrailPlanPopup = () => {
  //   if (checkForFreeTrailPopup()) {
  //     setTimeout(() => {
  //       if (checkForFreeTrailPopup()) {
  //         setShowPlanDetailsPopup(true);
  //         LocalStorage.setItem('showPlanDetailsPopupOnce', 'true');
  //       }
  //     }, 5000);
  //   }
  // };

  // const checkForFreeTrailPopup = () => {
  //   let showPopup = false;
  //   let isTrailPopupLoaded = LocalStorage.getItem('showPlanDetailsPopupOnce');
  //   if (!isTrailPopupLoaded) {
  //     //let route = Routes.PrivateRoute().find((a) => a.path === location.pathname.replace('/', ''));
  //     if (
  //       //  route &&
  //       !trailPlanDisplay?.has_subscription &&
  //       !trailPlanDisplay?.has_trail_plan &&
  //       !trailPlanDisplay.is_appsumo_user
  //     ) {
  //       showPopup = true;
  //     }
  //   }
  //   return showPopup;
  // };

  return (
    <>
      {isRouteLoaded && headerName && (
        <>
          <LiveChatNotificationBridge />
          <SupportAppNotification />
        </>
      )}
      {isRouteLoaded ? (
        isDesktop ? (
          <>
            {headerName && (
              <Header headerName={headerName} />
            )}
            {showSideBar ? (
              <div style={{ display: 'flex', flexDirection: 'row', minHeight: '100%', height: '100vh' }}>
                <ProfileSection handleChangeBrand={handleChangeBrand} handleInitCall={handleInitCall} />
                <MenuBarComponent
                  isMainMenuBarView={isMainMenuBarView}
                  menuItems={menuItems}
                  backButtonClick={backButtonClick}
                  handleChangeBrand={handleChangeBrand}
                  handleInitCall={handleInitCall}
                />
                <MainPage pathname={location.pathname} routes={routes} />
              </div>
            ) : (
              <MainPage pathname={location.pathname} routes={routes} />
            )}
          </>
        ) : (
          <>
            {headerName && <MobileHeader handleChangeBrand={handleChangeBrand} headerName={headerName} />}
            <MainPage isDesktop={false} pathname={location.pathname} routes={routes} />
            {headerName && (
              <Footer>
                <FooterSection />
              </Footer>
            )}
          </>
        )
      ) : (
        <>
          <Spinner />
        </>
      )}
    </>
  );
};

export default MainComponent;

